import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookingCategory } from "./booking-category";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { toast, ToastContainer } from "react-toastify";

interface Booking {
  id: string;
  booking_name: string;
  category: string;
  glam_spot: string;
  number_of_faces: number;
  date: string;
  time: string;
  status: string;
}

interface Stats {
  scheduled_bookings: number;
  total_orders: number;
  total_bookings: number;
  completed_bookings: number;
}

interface GlamPackage {
  id: string;
  title: string;
}

interface BookingItemProps {
  id: string;
  name: string;
  category: string;
  spot: string;
  faces: number;
  date: string;
  time: string;
  status: string;
  onClick?: (id: string) => void;
  onStatusUpdate?: (bookingId: string, status: string) => void;
}

interface BookingListProps {
  bookings: Booking[];
  glamPackages?: GlamPackage[];
  onStatusUpdate?: (bookingId: string, status: string) => void;
  isLoading: boolean;
  onBookingClick: (bookingId: string) => void;
}

// BookingItem component
function BookingItem({
  id,
  name,
  category,
  spot,
  faces,
  date,
  time,
  status,
  onClick,
  onStatusUpdate,
}: BookingItemProps) {
  const handleClick = () => {
    onClick?.(id);
  };

  const handleCompletedClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await onStatusUpdate?.(id, "completed");
      toast.success("Booking status updated to completed.", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to update booking status.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <div
      className="p-6 border border-gray-200 rounded-md cursor-pointer hover:border-[#B87775] transition-colors flex flex-col justify-between h-full"
      onClick={handleClick}
    >
      <div>
        <h3 className="text-lg font-semibold mb-4">{name}</h3>
        <div className="grid grid-cols-2 gap-y-4 text-sm">
          <div>
            <p className="text-muted-foreground">Glam Category</p>
            <p className="font-medium">{category}</p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground">Faces</p>
            <p className="font-medium">{faces}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Glam Spot</p>
            <p className="font-medium">
              {spot.includes("_session")
                ? "Studio"
                : spot.includes("_service")
                ? "Home"
                : spot}
            </p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground">Time</p>
            <p className="font-medium">{time}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Date</p>
            <p className="font-medium">{date}</p>
          </div>
        </div>
      </div>

      {/* Buttons Always at the Bottom */}
      <div className="flex gap-4 mt-6">
        {status === "scheduled" && (
          <Button
            variant="default"
            className="flex-1 text-[12px] bg-[#B87775] hover:bg-[#B87775]/90 text-white"
            // onClick={handleCompletedClick}
          >
            MARK AS COMPLETED
          </Button>
        )}
        {status === "completed" && (
          <Button
            variant="default"
            className="flex-1 text-[12px] bg-green-900 hover:bg-green-500/90 text-white"
            disabled
          >
            COMPLETED
          </Button>
        )}
        {status === "declined" && (
          <Button
            variant="default"
            className="flex-1 text-[12px] bg-red-700 hover:bg-red-500/90 text-white"
            disabled
          >
            DECLINED
          </Button>
        )}
      </div>
    </div>
  );
}

// BookingList component
export function BookingList({
  bookings,
  onBookingClick,
  onStatusUpdate,
  isLoading,
}: BookingListProps) {
  // const [showCategory, setShowCategory] = useState(false);
  // if (showCategory) {
  //   return <BookingCategory onBack={() => setShowCategory(false)} />;
  // }

  return (
    <Card>
      <ToastContainer />
      <div className="flex items-center justify-between mb-6 border-b">
        <div className="flex items-center gap-6 px-8 py-4">
          <div
            className={"cursor-pointer border-b-2 border-[#B87775] text-[#B87775]"}
          >
            BOOKINGS
          </div>
          {/* <div className="cursor-pointer" onClick={() => setShowCategory(true)}>
            GLAM PACKAGES
          </div> */}
        </div>
        <div className="flex items-center gap-4 p-4">
          <Button variant="outline" className="text-[#B87775] border-[#B87775]">
            1 - 8 Jan 2023
          </Button>
          <Button variant="outline" className="text-[#B87775] border-[#B87775]">
            Status
          </Button>
        </div>
      </div>
      <div className="px-8 py-4">
        {isLoading ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {bookings.map((booking) => (
              <BookingItem
                key={booking.id}
                id={booking.id}
                name={booking.booking_name}
                category={booking.category}
                spot={booking.glam_spot}
                faces={booking.number_of_faces}
                date={booking.date}
                time={booking.time}
                status={booking.status}
                onClick={onBookingClick}
                onStatusUpdate={onStatusUpdate}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
